//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useEffect } from 'react';
import { FormControlLabel, makeStyles, Radio, CircularProgress } from '@material-ui/core';
// import ClearRoundedIcon from '@material-ui/icons/ClearRounded';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getTierTitle } from '../../common/lovHelper';
import {
    getFormattedDate,
    //  getFormattedTime,
    getUserDateFormat,
} from '../../common/dateHelper';
import { retrieveStripSubscription, retrieveStripeUpcomingInvoice } from '../../common/services/paymentServices';
import { ADD_REMOVE_ADDITIONAL_SEATS, CANCEL_ALL, CHANGE_BILLING_INTERVAL, PROCEED_TO_PAYMENT, PURCHASE_ADDITIONAL_SEATS, SUBMIT_ORDER } from '../../common/constants';
import { getFakeStripeInvoiceForDisplay } from '../../common/paymentHelper';
import { numberWithCommas, getFormattedPrice } from '../../common/textHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { validatePromoCode } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdNumberFormat from '../../common/elemental/AfitdNumberFormat';
// import AfitdDatePicker from '../../common/elemental/AfitdDatePicker';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
// import AfitdButton from '../../common/elemental/AfitdButton';
// import AfitdIconButton from '../../common/elemental/AfitdIconButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { isInvalidPromoCode, isPromoCodeApplied } = params;
    return makeStyles(theme => ({
        inputBoxContainer: { display: 'flex' },
        inputBox: { flex: 1, padding: '0 5px', marginTop: 20 },
        active: { color: 'rgb(29,138,139)' },
        inactive: { color: 'rgba(17,30,20,0.4)' },
        inline: { display: 'flex', alignItems: 'center' },

        paypalContainer: { textAlign: 'center', background: 'rgb(240,242,246)', padding: 10, borderRadius: 4 },
        title: { fontWeight: 'bold', margin: '10px 0' },
        existingCardInfoContainer: { borderRadius: 4, border: '2px solid rgb(213,215,221)', padding: 20, margin: '5px 0' },
        cardInfoContainer: { borderRadius: 4, border: '2px solid rgb(213,215,221)', padding: 20, margin: '5px 0' },
        label: { margin: '20px 0 5px 0', fontWeight: 'bold' },

        billCycleContainer: {
            borderRadius: 4,
            border: '2px solid rgb(213,215,221)',
            padding: 20,
            marginTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        innerBillCycleContainer: { display: 'flex', alignItems: 'center' },
        cardPrice: { fontWeight: 'bold', fontSize: 21 },
        cardPriceSave: { padding: '4px 8px', background: 'rgba(252,130,31,0.2)', width: 'fit-content', borderRadius: 50, marginRight: 10 },

        invoiceContainer: { marginTop: 20, paddingTop: 20, borderTop: '2px solid rgb(240,242,246)', display: 'flex', flexDirection: 'column' },
        payingContainer: { marginTop: 20, paddingTop: 20, borderTop: '2px solid rgb(240,242,246)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        payingDetailsContainer: { display: 'flex', flexDirection: 'column' },
        payingDetailsLineTwo: { fontWeight: 'bold' },
        payingDetailsLineOne: { fontWeight: 'bold' },
        payingDetailsPayNowText: { fontWeight: 'bold', marginRight: 20 },

        qtyText: { marginRight: 10, fontWeight: 'bold' },

        cancelAdditionalSeatsButton: { color: 'rgb(252,130,31,1)', fontWeight: 'bold', cursor: 'pointer' },

        promoCodeContainer: {
            opacity: isPromoCodeApplied ? 0.3 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '15px',
        },
        promocode: {
            maxWidth: '60%',
            '& .MuiOutlinedInput-root': {
                height: '44px',
            },
        },
        promoCodeText: {
            fontSize: '16px',
            fontWeight: 'bold',
        },
        promoCodeMessageContainer: {
            fontSize: '14px',
        },
        promoCodeMessage: {
            color: isInvalidPromoCode ? 'red' : 'green',
            fontStyle: 'italic',
        },
        promocodeAppliedContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '10px',
            background: 'lightgrey',
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: isPromoCodeApplied && '#d3d3d35e',
            padding: '10px',
        },
        circularDiv: {
            textAlign: 'center',
            padding: '20px',
        },
        removeAllAdditionalSeatsContainer: {
            paddingTop: '10px',
        },
        lineItemDiv: {
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: '8px',
            paddingTop: '8px',
        },
        lineItemDescription: {
            position: 'relative',
            paddingLeft: '20px',
        },
        dot: {
            fontSize: '1.5rem',
            top: '3px',
            left: '0px',
            position: 'absolute',
        },
    }));
};

//---------------------------------------------------------------------
// Helper function
//---------------------------------------------------------------------
// // will format the expiry date
// function limit(val, max) {
//     if (val.length === 1 && val[0] > max[0]) val = '0' + val;

//     if (val.length === 2) {
//         if (Number(val) === 0) val = '01';
//         else if (val > max) val = max;
//     }
//     return val;
// }

// function checkCardExpiryDate(val) {
//     const month = limit(val.substring(0, 2), '12');
//     const year = val.substring(2, 4);

//     return month + (year.length ? '/' + year : '');
// }

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        stripeAdditionalSeatSubId,
        isAdditionalSeatOrder,
        isIntervalOrder,
        existingTier,
        existingMonthly,
        // existingAdditionalSeats,
        moreSeats,
        isUpgradeAdditionalSeat,
        isDowngradeAdditionalSeat,
        isDowngradeToMonthly,
        // userDateFormat,
        data,
        // setData,
        isSelectedPlanMonthly,
        setSelectedBillingPlan,
        handleChangeFields,
        handleClickSaveButton,
        handleCloseDialog,
        priceYr,
        priceMo,
        // seatPrice,
        promoCode,
        handleChangePromoCode,
        handleValidatePromoCode,
        // isInvalidPromoCode,
        // setIsInvalidPromoCode,
        // promoCodeMessage,
        // validPromoCodeData,
        // setPromoCodeMessage,
        isPromoCodeApplied,
        handleClickCancelAdditionalSeatsButton,
        billCycleEndDt,
        isSubmittingDowngrade,
        userDateFormat,
        newInvoiceTotal,
        isFetchingStripeInvoice,
    } = params;
    const { open: openPaymentDialog, selectedTier } = props;
    const additionalSeats = data?.additionalSeats;

    // const currentDate = new Date();

    const tier = _get(selectedTier, 'tier');
    const isCurrentTierSubscription = !isAdditionalSeatOrder && existingTier === tier && existingMonthly === isSelectedPlanMonthly;

    // dirty if additional seat has changed; tier order always dirty
    const isDirty = isAdditionalSeatOrder ? moreSeats !== 0 : true;

    const saveButtonDisabled = !isDirty || isCurrentTierSubscription || additionalSeats === '' || parseInt(additionalSeats) <= 0;

    // const { type } = validPromoCodeData;
    // let monthToAdd = 1;
    // if (type === '60D') monthToAdd = 2;

    const saveButtonLabel = isDowngradeAdditionalSeat || isDowngradeToMonthly ? SUBMIT_ORDER : PROCEED_TO_PAYMENT;

    const cancelAdditionalSeatsButtonIsHidden = !isAdditionalSeatOrder || !stripeAdditionalSeatSubId;

    const defns = {
        container: {
            open: openPaymentDialog,
            onClose: handleCloseDialog,
            label: isAdditionalSeatOrder
                ? stripeAdditionalSeatSubId
                    ? ADD_REMOVE_ADDITIONAL_SEATS
                    : PURCHASE_ADDITIONAL_SEATS
                : isIntervalOrder
                ? `${CHANGE_BILLING_INTERVAL} (Tier ${tier})`
                : existingTier === 0 // if first time purchase
                ? `Purchase ${getTierTitle(selectedTier?.tier)?.slice(0, -9)} (Tier ${selectedTier?.tier})`
                : `Upgrade to ${getTierTitle(selectedTier?.tier)?.slice(0, -9)} (Tier ${selectedTier?.tier})`,
            actionButtonDefns: {
                save: {
                    disabled: saveButtonDisabled || isSubmittingDowngrade || isFetchingStripeInvoice,
                    onClick: handleClickSaveButton,
                    color: 'primary',
                    label: isSubmittingDowngrade ? 'Submitting...' : saveButtonLabel,
                    loading: isSubmittingDowngrade,
                },
            },
        },
        // title: { variant: 'body1', value: 'some titles here' },
        label: { variant: 'body2' },
        promocode: {
            onChange: handleChangePromoCode,
            name: 'PromoCode',
            value: promoCode,
            placeholder: 'Enter your promo code',
            // error: isInvalidPromoCode,
            disabled: isPromoCodeApplied,
        },
        additionalSeats: { onChange: handleChangeFields, name: 'additionalSeats', value: additionalSeats, placeholder: '+ Ex 10' },

        cardPrice: { variant: 'body2', color: 'secondary' },
        cardPriceSpan: { variant: 'body2', component: 'span', color: 'secondary' },
        cardPriceSave: { variant: 'body2', color: 'primary' },

        payingDetailsLineOne: {
            variant: 'caption',
            isHidden: isUpgradeAdditionalSeat,
            value:
                isDowngradeToMonthly && billCycleEndDt && !isAdditionalSeatOrder
                    ? `You will be billed $${numberWithCommas(isSelectedPlanMonthly ? priceMo : priceYr)} ${isSelectedPlanMonthly && 'monthly'}`
                    : isAdditionalSeatOrder
                    ? `You will be billed $${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))} every month.`
                    : `You will be billed ${getFormattedPrice(isSelectedPlanMonthly ? priceMo : priceYr)}`,
        },
        payingDetailsLineTwo: {
            variant: 'caption',
            value:
                isDowngradeToMonthly && billCycleEndDt && !isAdditionalSeatOrder
                    ? ` starting on ${getFormattedDate(billCycleEndDt, userDateFormat)}. Cancel any time.`
                    : isAdditionalSeatOrder
                    ? `Cancel any time.`
                    : `every ${isSelectedPlanMonthly ? 'month' : 'year'}. Cancel any time.`,
        },
        payingDetailsPayNowText: { variant: 'body1', value: 'Paying now' },

        monthlyRadioButton: {
            value: 'monthly',
            label: 'Bill Monthly',
            control: <Radio disableRipple checked={isSelectedPlanMonthly} onChange={() => setSelectedBillingPlan('monthly')} />,
        },
        yearlyRadioButton: {
            value: 'yearly',
            label: 'Bill Yearly',
            control: <Radio disableRipple checked={!isSelectedPlanMonthly} onChange={() => setSelectedBillingPlan('yearly')} />,
        },
        additionalSeatsRadioButton: {
            label: 'Additional Seats',
            control: <Radio disableRipple checked />,
        },
        qtyText: { variant: 'body1', value: 'Qty' },
        applyBtn: {
            color: 'secondary',
            size: 'large',
            onClick: handleValidatePromoCode,
            disabled: isPromoCodeApplied,
        },
        cancelAdditionalSeatsButton: { variant: 'body1', value: CANCEL_ALL, isHidden: cancelAdditionalSeatsButtonIsHidden, onClick: handleClickCancelAdditionalSeatsButton },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
export default function OrderDialog(props) {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { currentEmployer, employerId } = appContext;
    const userDateFormat = getUserDateFormat(appContext);
    const existingTier = _get(currentEmployer, 'tier', 0);
    const existingMonthly = _get(currentEmployer, 'billMonthly');
    const billCycleEndDt = _get(currentEmployer, 'billCycleEndDt');
    const existingAdditionalSeats = _get(currentEmployer, 'additionalSeats', 0);
    const stripeCusId = currentEmployer?.stripeCusId;
    const stripeSubId = currentEmployer?.stripeSubId;
    const stripeAdditionalSeatSubId = currentEmployer?.stripeAdditionalSeatSubId;
    const trialAvailable = currentEmployer?.trialAvailable;

    // const layoutContext = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { open, selectedTier, /*shouldUpdate,*/ variant, onProceedToPayment, onSubmitDowngradeOrder, onClose, onCancelAdditionSeatsButtonClick } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [data, setData] = useState();
    const [selectedBillingPlan, setSelectedBillingPlan] = useState();
    const [promoCode, setPromoCode] = useState('');
    const [validPromoCodeData, setValidPromoCodeData] = useState({});
    const [isInvalidPromoCode, setIsInvalidPromoCode] = useState(false);
    const [promoCodeMessage, setPromoCodeMessage] = useState('');
    const [upcomingInvoices, setUpcomingInvoices] = useState();
    const [stripeSubscription, setStripeSubscription] = useState();
    const [stripeAdditionalSeatSubscription, setStripeAdditionalSeatSubscription] = useState();
    const [isFetchingStripeInvoice, setIsFetchingStripeInvoice] = useState(false);
    const [isSubmittingDowngrade, setIsSubmittingDowngrade] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isAdditionalSeatOrder = variant === 'additional-seat';
    const isIntervalOrder = variant === 'interval';
    let priceMo = parseFloat(_get(selectedTier, 'priceMo', '')).toFixed(2);
    let priceYr = parseFloat(_get(selectedTier, 'priceYr', '')).toFixed(2);

    const seatPrice = parseFloat(_get(selectedTier, 'seatPrice', '')).toFixed(2);
    const tier = _get(selectedTier, 'tier', 0);

    const defaultAdditionalSeats = stripeAdditionalSeatSubId ? existingAdditionalSeats : 1;
    const additionalSeats = data?.additionalSeats;
    const moreSeats = _isNil(additionalSeats) ? 0 : parseInt(additionalSeats) - parseInt(existingAdditionalSeats);
    const isUpgradeAdditionalSeat = moreSeats > 0;
    const isDowngradeAdditionalSeat = moreSeats < 0;
    const isSelectedPlanMonthly = selectedBillingPlan === 'monthly';
    const isDowngradeToMonthly = stripeSubId && !existingMonthly && isSelectedPlanMonthly;

    const stripeTierProductName = _get(stripeSubscription, 'items.data[0].price.product.name');
    const stripeTierProductUnitAmount = _get(stripeSubscription, 'items.data[0].price.unit_amount');
    const stripeSubscriptionItemId = _get(stripeSubscription, 'items.data[0].id');
    const stripeTierCurrentPeriodEnd = stripeSubscription?.current_period_end;
    const stripeAdditionalSeatSubscriptionItemId = _get(stripeAdditionalSeatSubscription, 'items.data[0].id');
    const stripeAdditionalSeatProductName = _get(stripeAdditionalSeatSubscription, 'items.data[0].price.product.name');
    const stripeAdditionalSeatProductUnitAmount = _get(stripeAdditionalSeatSubscription, 'items.data[0].price.unit_amount');

    const newInvoice = upcomingInvoices?.newInvoice;
    // const invoiceSubscriptionProrationDate = newInvoice?.subscription_proration_date; // in seconds
    // const newInvoiceSubtotal = newInvoice?.subtotal || 0;
    const newInvoiceTotal = newInvoice?.total || 0;
    const newInvoiceLineItems = _get(newInvoice, 'lines.data', []);
    // const newInvoiceSectionVisible = newInvoiceLineItems.length > 0;
    const newInvoiceSubscriptionPeriodStartDt = _get(newInvoice, 'subscription.current_period_start', 0);
    // const newInvoiceSubscriptionPeriodEndDt = _get(newInvoice, 'subscription.current_period_end', 0);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // initialize monthly/yearly field
    useEffect(() => {
        if (open) {
            if (variant === 'interval') setSelectedBillingPlan(existingMonthly ? 'yearly' : 'monthly');
            else setSelectedBillingPlan(existingMonthly ? 'monthly' : 'yearly');
        }
    }, [open, variant, existingMonthly]);

    // initialize additional seat field
    useEffect(() => {
        if (open) {
            setData(prev => ({ ...prev, additionalSeats: defaultAdditionalSeats }));
        }
    }, [open, defaultAdditionalSeats]);

    // load Stripe subscription data for Tier
    useEffect(() => {
        const retrieveSubscription = async () => {
            const retrieveSubscriptionResp = await retrieveStripSubscription({ subscriptionId: stripeSubId });
            retrieveSubscriptionResp && setStripeSubscription(retrieveSubscriptionResp.data);
        };
        if (open && !isAdditionalSeatOrder && stripeSubId) {
            retrieveSubscription();
        }
    }, [open, isAdditionalSeatOrder, stripeSubId]);

    // load Stripe subscription data for Additional Seat
    useEffect(() => {
        const retrieveSubscription = async () => {
            const retrieveSubscriptionResp = await retrieveStripSubscription({ subscriptionId: stripeAdditionalSeatSubId });
            retrieveSubscriptionResp && setStripeAdditionalSeatSubscription(retrieveSubscriptionResp.data);
        };
        if (open && isAdditionalSeatOrder && stripeAdditionalSeatSubId) {
            retrieveSubscription();
        }
    }, [open, isAdditionalSeatOrder, stripeAdditionalSeatSubId]);

    // preview Stripe invoice for tier product
    useEffect(() => {
        const retrieveUpcomingInvoice = async () => {
            setIsFetchingStripeInvoice(true);
            const retrieveInvoiceResp = await retrieveStripeUpcomingInvoice({
                stripeCusId,
                stripeSubId,
                stripeSubscriptionItemId,
                existingTier,
                existingMonthly,
                tier,
                monthly: isSelectedPlanMonthly,
                quantity: 1,
                trialAvailable,
            });
            if (retrieveInvoiceResp) {
                setIsFetchingStripeInvoice(false);
                setUpcomingInvoices(retrieveInvoiceResp);
            }
        };

        if (open && stripeCusId && tier && !isAdditionalSeatOrder) {
            if (!stripeSubId && !stripeSubscription && tier !== 0) {
                // new tier subscription
                retrieveUpcomingInvoice();
            } else if (stripeSubscription && (tier > existingTier || (tier >= existingTier && existingMonthly && !isSelectedPlanMonthly))) {
                // upgrade tier or change from monthly to yearly
                retrieveUpcomingInvoice();
            } else if ((stripeSubscription && tier < existingTier) || (stripeSubscription && tier === existingTier && !existingMonthly && isSelectedPlanMonthly)) {
                // downgrade tier or change from yearly to monthly within the same tier
                // downgrade is a subscription schedule and invoice cannot be previewed
                // set up a fake invoice for display
                const description = `${stripeTierProductName} ($${stripeTierProductUnitAmount / 100}/mo)`;
                const currentPeriodStart = stripeTierCurrentPeriodEnd; // next invoice starts when current period ends
                setUpcomingInvoices(getFakeStripeInvoiceForDisplay({ description, amount: stripeTierProductUnitAmount, currentPeriodStart }));
            } else {
                setUpcomingInvoices(null);
            }
        }
    }, [
        open,
        stripeCusId,
        stripeSubId,
        existingTier,
        existingMonthly,
        tier,
        isSelectedPlanMonthly,
        stripeSubscription,
        stripeSubscriptionItemId,
        stripeTierCurrentPeriodEnd,
        stripeTierProductName,
        stripeTierProductUnitAmount,
        isAdditionalSeatOrder,
        trialAvailable,
    ]);

    // preview Stripe invoice data for additional seat product
    useEffect(() => {
        const retrieveUpcomingInvoice = async () => {
            setIsFetchingStripeInvoice(true);
            const retrieveInvoiceResp = await retrieveStripeUpcomingInvoice({
                stripeCusId,
                stripeAdditionalSeatSubId,
                stripeAdditionalSeatSubscriptionItemId,
                isAdditionalSeat: true,
                existingQuantity: existingAdditionalSeats,
                quantity: parseInt(additionalSeats),
                trialAvailable,
            });
            if (retrieveInvoiceResp) {
                setIsFetchingStripeInvoice(false);
                setUpcomingInvoices(retrieveInvoiceResp);
            }
        };

        if (open && stripeCusId && isAdditionalSeatOrder && !_isNil(additionalSeats)) {
            if (!isNaN(moreSeats) && moreSeats > 0) {
                retrieveUpcomingInvoice();
            } else if (stripeAdditionalSeatSubscription && !isNaN(moreSeats) && moreSeats < 0) {
                // downgrade is a subscription schedule and invoice cannot be previewed
                // set up a fake invoice for display
                const description = `${additionalSeats} x ${stripeAdditionalSeatProductName} ($${stripeAdditionalSeatProductUnitAmount / 100}/mo)`;
                const amount = seatPrice * additionalSeats * 100;
                const currentPeriodStart = stripeAdditionalSeatSubscription.current_period_end; // next invoice starts when current period ends
                setUpcomingInvoices(getFakeStripeInvoiceForDisplay({ description, amount, currentPeriodStart }));
            } else {
                // no change. Clear the previously loaded upcoming invoice (if any)
                setUpcomingInvoices(null);
            }
        }
    }, [
        open,
        stripeCusId,
        isSelectedPlanMonthly,
        isAdditionalSeatOrder,
        stripeAdditionalSeatSubId,
        stripeAdditionalSeatSubscriptionItemId,
        stripeAdditionalSeatSubscription,
        additionalSeats,
        moreSeats,
        existingAdditionalSeats,
        seatPrice,
        stripeAdditionalSeatProductName,
        stripeAdditionalSeatProductUnitAmount,
        trialAvailable,
    ]);

    //---------------------------------------------------------------------
    // Changing Price if Promo code is applied
    //---------------------------------------------------------------------
    const isPromoCodeApplied = Boolean(promoCodeMessage === 'Applied');

    if (isPromoCodeApplied) {
        if (validPromoCodeData?.type === '10%') {
            priceMo = (priceMo - parseFloat((priceMo / 100) * 10)).toFixed(2);
            priceYr = (priceYr - parseFloat((priceYr / 100) * 10)).toFixed(2);
        }
    }

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData({ ...data, [name]: value });
    };

    const handleChangePromoCode = evt => {
        setPromoCodeMessage('');
        const { value } = evt.target;
        setPromoCode(value);
    };

    const handleClickSaveButton = async () => {
        // reset states
        clearFields();

        if (isDowngradeAdditionalSeat || isDowngradeToMonthly) {
            //! assuming here is only for downgrading existing seats or SAME tier changing from yearly to monthly
            if (onSubmitDowngradeOrder) {
                setIsSubmittingDowngrade(true);
                await onSubmitDowngradeOrder({
                    isAdditionalSeatOrder,
                    tierNew: existingTier, // same tier
                    billMonthlyNew: isSelectedPlanMonthly,
                    additionalSeatsNew: parseInt(additionalSeats),
                    employerId,
                    stripeSubId,
                    stripeAdditionalSeatSubId,
                    existingTier,
                    existingMonthly,
                    existingAdditionalSeats,
                    setIsSubmittingDowngrade,
                });
            }
        } else {
            // fire event
            onProceedToPayment &&
                onProceedToPayment({
                    invoices: upcomingInvoices,
                    monthly: isSelectedPlanMonthly,
                    isAdditionalSeatOrder,
                });
        }
    };

    const handleValidatePromoCode = async () => {
        if (promoCode !== '') {
            const res = await validatePromoCode({ promoCode, setValidPromoCodeData });
            setIsInvalidPromoCode(false);
            setPromoCodeMessage('Applied');
            if (res?.error) {
                setIsInvalidPromoCode(true);
                setPromoCodeMessage('Invalid');
            }
        }
    };
    // const handleClearPromoCode = () => {
    //     setPromoCodeMessage('');
    //     setIsInvalidPromoCode(false);
    //     setValidPromoCodeData({});
    //     setPromoCode('');
    // };

    const handleCloseDialog = () => {
        clearFields();
        onClose && onClose();
    };

    const clearFields = () => {
        setData(null);
        setPromoCodeMessage('');
        setIsInvalidPromoCode(false);
        setValidPromoCodeData({});
        setPromoCode('');
        setStripeSubscription(null);
        setStripeAdditionalSeatSubscription(null);
        setUpcomingInvoices(null);
    };

    const handleClickCancelAdditionalSeatsButton = evt => {
        onCancelAdditionSeatsButtonClick && onCancelAdditionSeatsButtonClick(evt);
        handleCloseDialog();
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isInvalidPromoCode, isPromoCodeApplied })();
    const defns = getDefns({
        classes,
        props,
        stripeAdditionalSeatSubId,
        isAdditionalSeatOrder,
        isIntervalOrder,
        existingTier,
        existingMonthly,
        existingAdditionalSeats,
        moreSeats,
        isUpgradeAdditionalSeat,
        isDowngradeAdditionalSeat,
        isDowngradeToMonthly,
        userDateFormat,
        data,
        setData,
        isSelectedPlanMonthly,
        setSelectedBillingPlan,
        handleChangeFields,
        handleClickSaveButton,
        handleCloseDialog,
        priceYr,
        priceMo,
        seatPrice,
        promoCode,
        handleChangePromoCode,
        handleValidatePromoCode,
        isInvalidPromoCode,
        setIsInvalidPromoCode,
        promoCodeMessage,
        validPromoCodeData,
        setPromoCodeMessage,
        isPromoCodeApplied,
        handleClickCancelAdditionalSeatsButton,
        billCycleEndDt,
        isSubmittingDowngrade,
        newInvoiceTotal,
        isFetchingStripeInvoice,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog {...defns.container}>
            {/* <AfitdTypography {...defns.title} /> */}
            {!isAdditionalSeatOrder && (!stripeSubId || (tier === existingTier && !existingMonthly) || (tier > existingTier && existingMonthly)) && (
                <>
                    <div {...defns.billCycleContainer}>
                        <FormControlLabel {...defns.monthlyRadioButton} />
                        <div {...defns.innerBillCycleContainer}>
                            <AfitdTypography {...defns.cardPrice}>
                                ${numberWithCommas(priceMo)}
                                <AfitdTypography {...defns.cardPriceSpan}>/mo</AfitdTypography>
                            </AfitdTypography>
                        </div>
                    </div>
                </>
            )}
            {!isAdditionalSeatOrder && (!stripeSubId || (tier === existingTier && existingMonthly) || tier !== existingTier) && (
                <>
                    <div {...defns.billCycleContainer}>
                        <FormControlLabel {...defns.yearlyRadioButton} />
                        <div {...defns.innerBillCycleContainer}>
                            <AfitdTypography {...defns.cardPriceSave}>SAVE ${numberWithCommas(parseInt(priceMo * 12 - priceYr))}</AfitdTypography>
                            <AfitdTypography {...defns.cardPrice}>
                                ${numberWithCommas(priceYr)}
                                <AfitdTypography {...defns.cardPriceSpan}>/yr</AfitdTypography>
                            </AfitdTypography>
                        </div>
                    </div>
                </>
            )}
            {isAdditionalSeatOrder && (
                <div {...defns.billCycleContainer}>
                    <FormControlLabel {...defns.additionalSeatsRadioButton} />
                    <div {...defns.innerBillCycleContainer}>
                        <AfitdTypography {...defns.qtyText} />
                        {/* <AfitdTextField {...defns.additionalSeats} /> */}
                        <AfitdNumberFormat
                            placeholder="+ Ex 10"
                            value={additionalSeats}
                            customInput={AfitdTextField}
                            onValueChange={values => setData({ ...data, additionalSeats: values.value })}
                            prefix={''}
                            type="text"
                            allowNegative={false}
                            isNumericString={true}
                            defaultValue={0}
                            decimalSeparator={false}
                        />
                    </div>
                    <AfitdTypography {...defns.cardPrice}>
                        ${seatPrice}
                        <AfitdTypography {...defns.cardPriceSpan}>/mo</AfitdTypography>
                    </AfitdTypography>
                </div>
            )}
            {isAdditionalSeatOrder && (
                <div {...defns.removeAllAdditionalSeatsContainer}>
                    <AfitdTypography {...defns.cancelAdditionalSeatsButton} />
                </div>
            )}
            {/* {!shouldUpdate && (
                <div {...defns.promoCodeContainer}>
                    <AfitdTypography {...defns.promoCodeText}>Promo Code:</AfitdTypography>
                    <AfitdTextField {...defns.promocode} />
                    <AfitdButton {...defns.applyBtn}>Apply</AfitdButton>
                </div>
            )} */}
            {/* {promoCodeMessage !== '' && (
                <div {...defns.promocodeAppliedContainer}>
                    <AfitdTypography {...defns.promoCodeMessageContainer}>
                        Promo Code: <b>{promoCode}</b> <span {...defns.promoCodeMessage}>{promoCodeMessage}</span>
                    </AfitdTypography>
                    {isPromoCodeApplied && (
                        <AfitdIconButton onClick={handleClearPromoCode}>
                            <ClearRoundedIcon />
                        </AfitdIconButton>
                    )}
                </div>
            )} */}
            {isFetchingStripeInvoice ? (
                <div {...defns.circularDiv}>
                    <CircularProgress size={40} />
                </div>
            ) : (
                !isDowngradeToMonthly &&
                upcomingInvoices && (
                    <div {...defns.invoiceContainer}>
                        {!stripeAdditionalSeatSubId && isAdditionalSeatOrder && <AfitdTypography value={`You are buying ${moreSeats} seat${moreSeats > 1 ? 's' : ''}`} />}
                        {stripeAdditionalSeatSubId && isAdditionalSeatOrder && isUpgradeAdditionalSeat && (
                            <AfitdTypography value={`You are adding ${moreSeats} more seat${moreSeats > 1 ? 's' : ''}`} />
                        )}
                        {stripeAdditionalSeatSubId && isAdditionalSeatOrder && isDowngradeAdditionalSeat && (
                            <AfitdTypography value={`You are removing ${Math.abs(moreSeats)} seat${Math.abs(moreSeats) > 1 ? 's' : ''}`} />
                        )}

                        {!isDowngradeAdditionalSeat && (
                            <>
                                <br />
                                <AfitdTypography variant="h6">Upcoming Invoice Line items</AfitdTypography>
                                <br />

                                {_map(newInvoiceLineItems, lineItem => {
                                    return (
                                        <div {...defns.lineItemDiv}>
                                            <AfitdTypography {...defns.lineItemDescription}>
                                                {' '}
                                                <span {...defns.dot}>&bull;</span> {lineItem.description}
                                            </AfitdTypography>
                                            <AfitdTypography align="right">{getFormattedPrice(parseFloat(lineItem.amount / 100).toFixed(2))}</AfitdTypography>
                                        </div>
                                    );
                                })}
                                <div>
                                    <hr />
                                    <AfitdTypography align="right">
                                        <b>Total = ${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}</b>
                                    </AfitdTypography>
                                </div>
                                {!isIntervalOrder && tier < existingTier && (
                                    <div>
                                        {/* <br /> */}
                                        <AfitdTypography value={` Next Invoice Date: ${getFormattedDate(newInvoiceSubscriptionPeriodStartDt * 1000, userDateFormat)}`} />
                                    </div>
                                )}
                            </>
                        )}
                        {/* <div>Sub-total: <AfitdTypography value={newInvoiceSubtotal / 100} /></div> */}
                        {/* <div>
                        Billing period (time is for our reference only):
                        <AfitdTypography
                            value={`${getFormattedDate(newInvoiceSubscriptionPeriodStartDt * 1000)} ${getFormattedTime(
                                newInvoiceSubscriptionPeriodStartDt * 1000,
                            )} to ${getFormattedDate(newInvoiceSubscriptionPeriodEndDt * 1000)} ${getFormattedTime(newInvoiceSubscriptionPeriodEndDt * 1000)}`}
                        />
                    </div> */}
                    </div>
                )
            )}
            {!isFetchingStripeInvoice && (
                <div {...defns.payingContainer}>
                    <div {...defns.payingDetailsContainer}>
                        <AfitdTypography {...defns.payingDetailsLineOne} />
                        <AfitdTypography {...defns.payingDetailsLineTwo} />
                    </div>
                    <div {...defns.inline}>
                        <AfitdTypography {...defns.payingDetailsPayNowText} />
                        {isAdditionalSeatOrder && !isDowngradeAdditionalSeat ? (
                            <AfitdTypography {...defns.cardPrice}>
                                ${numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}
                                <AfitdTypography {...defns.cardPriceSpan}>/mo</AfitdTypography>
                            </AfitdTypography>
                        ) : isSelectedPlanMonthly ? (
                            <AfitdTypography {...defns.cardPrice}>
                                ${isDowngradeToMonthly ? numberWithCommas(priceMo) : numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}
                                <AfitdTypography {...defns.cardPriceSpan}>/mo</AfitdTypography>
                            </AfitdTypography>
                        ) : (
                            <AfitdTypography {...defns.cardPrice}>
                                ${isDowngradeToMonthly ? numberWithCommas(priceYr) : numberWithCommas(parseFloat(newInvoiceTotal / 100).toFixed(2))}
                                <AfitdTypography {...defns.cardPriceSpan}>/yr</AfitdTypography>
                            </AfitdTypography>
                        )}
                    </div>
                </div>
            )}
        </AfitdEditDialog>
    );
}
